import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus } from "lucide-react";
import { adminApi, type AdminPromo, type CreatePromoInput, type UpdatePromoInput } from "@/api/admin";
import { Dialog } from "@/components/ui/Dialog";
import { ApiError } from "@/api/client";

export const Route = createFileRoute("/admin/promos")({
  component: AdminPromosPage,
});

function AdminPromosPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-promos"], queryFn: adminApi.listPromos });
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (p: AdminPromo) => adminApi.updatePromo(p.id, { active: !p.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-promos"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Promo codes</h1>
          <p className="mt-1 text-ink/65">
            Discount codes for signup &amp; renewals. Track entries vs redemptions and see who's using each.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-primary">
          <Plus size={16} className="mr-1" /> New promo
        </button>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Plans</th>
              <th className="px-4 py-3">Enters</th>
              <th className="px-4 py-3">Redeems</th>
              <th className="px-4 py-3">Valid until</th>
              <th className="px-4 py-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            ) : data?.promos.length ? (
              data.promos.map((p) => (
                <tr key={p.id} className="border-t border-ink/8 hover:bg-cream">
                  <td className="px-4 py-3">
                    <button onClick={() => setDetailId(p.id)} className="font-medium text-primary-600 hover:underline">
                      {p.code}
                    </button>
                    {p.new_project_only && (
                      <span className="ml-2 rounded bg-ink/8 px-1.5 py-0.5 text-[10px] uppercase text-ink/55">new-only</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.kind === "percent" ? `${p.value}%` : `$${p.value}`} off
                  </td>
                  <td className="px-4 py-3">{p.scope}</td>
                  <td className="px-4 py-3 text-ink/65">
                    {p.plan_allowlist && p.plan_allowlist.length ? p.plan_allowlist.join(", ") : "any"}
                  </td>
                  <td className="px-4 py-3 text-ink/65">
                    {p.enter_count}{p.max_enters !== null ? ` / ${p.max_enters}` : ""}
                  </td>
                  <td className="px-4 py-3 text-ink/65">
                    {p.redeem_count}{p.max_redemptions !== null ? ` / ${p.max_redemptions}` : ""}
                  </td>
                  <td className="px-4 py-3 text-ink/55">
                    {p.valid_until ? new Date(p.valid_until).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggle.mutate(p)}
                      disabled={toggle.isPending}
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        p.active ? "bg-green-500/15 text-green-600" : "bg-ink/8 text-ink/55"
                      }`}
                    >
                      {p.active ? "active" : "disabled"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={8} className="px-4 py-6 text-ink/55">No promo codes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreatePromoDialog open={createOpen} onOpenChange={setCreateOpen} />
      {detailId && <PromoDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function CreatePromoDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreatePromoInput>({
    code: "",
    kind: "percent",
    value: "",
    scope: "once",
    new_project_only: false,
    per_user_limit: 1,
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      adminApi.createPromo({
        ...form,
        code: form.code.trim(),
        plan_allowlist:
          typeof form.plan_allowlist === "string" && (form.plan_allowlist as string).trim()
            ? (form.plan_allowlist as unknown as string).split(",").map((s) => s.trim()).filter(Boolean)
            : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-promos"] });
      onOpenChange(false);
      setForm({ code: "", kind: "percent", value: "", scope: "once", new_project_only: false, per_user_limit: 1 });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed to create promo"),
  });

  const set = <K extends keyof CreatePromoInput>(k: K, v: CreatePromoInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New promo code" description="Discount applied at checkout / renewal.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          if (!form.code.trim() || !form.value.trim()) return;
          m.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Code</label>
            <input className="input mt-1" value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="EARLY30" required />
          </div>
          <div>
            <label className="label">Scope</label>
            <select className="input mt-1" value={form.scope} onChange={(e) => set("scope", e.target.value as CreatePromoInput["scope"])}>
              <option value="once">once (one-time)</option>
              <option value="lifetime">lifetime (recurring)</option>
            </select>
          </div>
          <div>
            <label className="label">Kind</label>
            <select className="input mt-1" value={form.kind} onChange={(e) => set("kind", e.target.value as CreatePromoInput["kind"])}>
              <option value="percent">percent (%)</option>
              <option value="fixed_usd">fixed (USDC)</option>
            </select>
          </div>
          <div>
            <label className="label">Value</label>
            <input className="input mt-1" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder={form.kind === "percent" ? "30" : "20.00"} required />
          </div>
          <div>
            <label className="label">Per-user limit</label>
            <input
              type="number"
              min={1}
              className="input mt-1"
              value={form.per_user_limit ?? 1}
              onChange={(e) => set("per_user_limit", Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Max redemptions</label>
            <input
              type="number"
              min={0}
              className="input mt-1"
              placeholder="∞"
              value={form.max_redemptions ?? ""}
              onChange={(e) => set("max_redemptions", e.target.value === "" ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Max entries</label>
            <input
              type="number"
              min={0}
              className="input mt-1"
              placeholder="∞"
              value={form.max_enters ?? ""}
              onChange={(e) => set("max_enters", e.target.value === "" ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Valid until</label>
            <input
              type="date"
              className="input mt-1"
              value={form.valid_until ? form.valid_until.slice(0, 10) : ""}
              onChange={(e) => set("valid_until", e.target.value ? new Date(e.target.value).toISOString() : null)}
            />
          </div>
        </div>
        <div>
          <label className="label">Plan allowlist <span className="text-ink/45">(comma-separated plan ids; blank = any)</span></label>
          <input
            className="input mt-1"
            placeholder="pro, scale"
            value={(form.plan_allowlist as unknown as string) ?? ""}
            onChange={(e) => set("plan_allowlist", e.target.value as unknown as string[])}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.new_project_only} onChange={(e) => set("new_project_only", e.target.checked)} />
          New projects only (signup, not renewals)
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>
            {m.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function PromoDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["admin-promo", id], queryFn: () => adminApi.getPromo(id) });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={data ? data.promo.code : "Promo"} description={editing ? "Edit promo" : "Usage overview"}>
      {isLoading || !data ? (
        <p className="text-sm text-ink/55">Loading…</p>
      ) : editing ? (
        <PromoEditForm
          promo={data.promo}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            qc.invalidateQueries({ queryKey: ["admin-promo", id] });
            qc.invalidateQueries({ queryKey: ["admin-promos"] });
          }}
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Mini label="Entered" value={String(data.promo.enter_count)} />
            <Mini label="Redeemed" value={String(data.promo.redeem_count)} />
            <Mini label="Total discount" value={`$${Number(data.total_discount_usd).toLocaleString()}`} />
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-ink/8 p-4 text-sm">
            <Row label="Discount">
              {data.promo.kind === "percent" ? `${data.promo.value}%` : `$${data.promo.value}`} off
            </Row>
            <Row label="Scope">{data.promo.scope}</Row>
            <Row label="Eligible plans">
              {data.promo.plan_allowlist && data.promo.plan_allowlist.length
                ? data.promo.plan_allowlist.join(", ")
                : "any plan"}
            </Row>
            <Row label="New projects only">{data.promo.new_project_only ? "yes" : "no"}</Row>
            <Row label="Per-user limit">{data.promo.per_user_limit}</Row>
            <Row label="Max redemptions">{data.promo.max_redemptions ?? "∞"}</Row>
            <Row label="Max entries">{data.promo.max_enters ?? "∞"}</Row>
            <Row label="Active">{data.promo.active ? "yes" : "no"}</Row>
            <Row label="Valid from">
              {data.promo.valid_from ? new Date(data.promo.valid_from).toLocaleString() : "—"}
            </Row>
            <Row label="Valid until">
              {data.promo.valid_until ? new Date(data.promo.valid_until).toLocaleString() : "—"}
            </Row>
          </dl>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
                <tr>
                  <th className="px-3 py-2">Project</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Discount</th>
                  <th className="px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {data.usage.length ? data.usage.map((u) => (
                  <tr key={u.redemption_id} className="border-t border-ink/8">
                    <td className="px-3 py-2">{u.project_name ?? <span className="text-ink/45">(pending)</span>}</td>
                    <td className="px-3 py-2 text-ink/65">{u.user_email ?? "—"}</td>
                    <td className="px-3 py-2">{u.status}</td>
                    <td className="px-3 py-2">${Number(u.discount_usd)}</td>
                    <td className="px-3 py-2 text-ink/55">{new Date(u.redeemed_at ?? u.entered_at).toLocaleDateString()}</td>
                  </tr>
                )) : <tr><td colSpan={5} className="px-3 py-4 text-ink/55">No usage yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary">Close</button>
            <button onClick={() => setEditing(true)} className="btn-primary">Edit</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PromoEditForm({
  promo,
  onCancel,
  onSaved,
}: {
  promo: AdminPromo;
  onCancel: () => void;
  onSaved: () => void;
}) {
  // `code` is immutable (it's stamped on invoices); everything else is editable.
  const [f, setF] = useState({
    kind: promo.kind,
    value: promo.value,
    scope: promo.scope,
    new_project_only: promo.new_project_only,
    plan_allowlist: (promo.plan_allowlist ?? []).join(", "),
    per_user_limit: promo.per_user_limit,
    max_enters: promo.max_enters,
    max_redemptions: promo.max_redemptions,
    valid_from: promo.valid_from,
    valid_until: promo.valid_until,
    active: promo.active,
  });
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));

  const m = useMutation({
    mutationFn: () => {
      const patch: UpdatePromoInput = {
        kind: f.kind,
        value: f.value,
        scope: f.scope,
        new_project_only: f.new_project_only,
        // blank → null ("any plan"); the API also sanitizes server-side.
        plan_allowlist: f.plan_allowlist.trim()
          ? f.plan_allowlist.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        per_user_limit: f.per_user_limit,
        max_enters: f.max_enters,
        max_redemptions: f.max_redemptions,
        valid_from: f.valid_from,
        valid_until: f.valid_until,
        active: f.active,
      };
      return adminApi.updatePromo(promo.id, patch);
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed to save"),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErr(null);
        m.mutate();
      }}
      className="space-y-4"
    >
      <div className="text-sm text-ink/55">
        Code <span className="font-mono font-medium text-ink">{promo.code}</span> (not editable)
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Kind</label>
          <select className="input mt-1" value={f.kind} onChange={(e) => set("kind", e.target.value as AdminPromo["kind"])}>
            <option value="percent">percent (%)</option>
            <option value="fixed_usd">fixed (USDC)</option>
          </select>
        </div>
        <div>
          <label className="label">Value</label>
          <input className="input mt-1" value={f.value} onChange={(e) => set("value", e.target.value)} />
        </div>
        <div>
          <label className="label">Scope</label>
          <select className="input mt-1" value={f.scope} onChange={(e) => set("scope", e.target.value as AdminPromo["scope"])}>
            <option value="once">once (one-time)</option>
            <option value="lifetime">lifetime (recurring)</option>
          </select>
        </div>
        <div>
          <label className="label">Per-user limit</label>
          <input
            type="number"
            min={1}
            className="input mt-1"
            value={f.per_user_limit}
            onChange={(e) => set("per_user_limit", Number(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Max redemptions</label>
          <input
            type="number"
            min={0}
            className="input mt-1"
            placeholder="∞"
            value={f.max_redemptions ?? ""}
            onChange={(e) => set("max_redemptions", e.target.value === "" ? null : Number(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Max entries</label>
          <input
            type="number"
            min={0}
            className="input mt-1"
            placeholder="∞"
            value={f.max_enters ?? ""}
            onChange={(e) => set("max_enters", e.target.value === "" ? null : Number(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Valid from</label>
          <input
            type="date"
            className="input mt-1"
            value={f.valid_from ? f.valid_from.slice(0, 10) : ""}
            onChange={(e) => set("valid_from", e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
        </div>
        <div>
          <label className="label">Valid until</label>
          <input
            type="date"
            className="input mt-1"
            value={f.valid_until ? f.valid_until.slice(0, 10) : ""}
            onChange={(e) => set("valid_until", e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
        </div>
      </div>
      <div>
        <label className="label">Eligible plans <span className="text-ink/45">(comma-separated plan ids; blank = any)</span></label>
        <input
          className="input mt-1"
          placeholder="wallet, pro"
          value={f.plan_allowlist}
          onChange={(e) => set("plan_allowlist", e.target.value)}
        />
      </div>
      <div className="flex items-center gap-6 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={f.new_project_only} onChange={(e) => set("new_project_only", e.target.checked)} />
          New projects only
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
          Active
        </label>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" disabled={m.isPending}>
          {m.isPending ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink/55">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="card py-3">
      <div className="text-xs uppercase tracking-wide text-ink/55">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}
